import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import IMG from "@/lib/images";
import {
  Upload, Wand2, Layers, Image as ImageIcon, Download,
  ChevronRight, Gem, Settings, Sparkles, Lock,
} from "lucide-react";

const STYLES = ["Jadau", "Polki", "Meenakari", "Temple", "Antique", "Bridal", "Lightweight", "Modern Fusion"];
const TABS = ["Front", "Side", "Top", "Back"];
const STONES = [
  { name: "Polki", color: "#F5F5F5" },
  { name: "Emerald", color: "#0E6B4F" },
  { name: "Ruby", color: "#8B0E1F" },
  { name: "Sapphire", color: "#1B3A8C" },
  { name: "Pearl", color: "#E8E2D6" },
];

const STYLE_IMG = {
  Jadau: IMG.jadau,
  Polki: IMG.kundan,
  Meenakari: IMG.meenakari,
  Temple: IMG.temple,
  Antique: IMG.filigree,
  Bridal: IMG.bridal,
  Lightweight: IMG.bangle,
  "Modern Fusion": IMG.ring,
};

export default function DashboardPreview() {
  const [activeStyle, setActiveStyle] = useState("Jadau");
  const [activeView, setActiveView] = useState("Front");
  const [prompt, setPrompt] = useState("");

  const handleLockedAction = () => {
    toast.message("Sign in to use the studio", {
      description: "Create an account (free) and use Gemini Nano Banana to generate renders — 10 credits on signup.",
    });
  };

  const currentImage = STYLE_IMG[activeStyle] || IMG.jadau;

  return (
    <section className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 -z-10 radial-gold-glow opacity-50" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-12 md:mb-16 flex items-end justify-between gap-4"
        >
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-5">Dashboard</p>
            <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] text-white">
              The Facetra Studio — <span className="italic gold-gradient-text">try the surface</span>.
            </h2>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1 }}
          className="relative"
        >
          <div className="absolute -inset-x-10 -top-10 -bottom-10 -z-10 rounded-[40px] bg-gradient-to-b from-[#D4AF37]/8 to-transparent blur-3xl" />

          <div className="glass-panel rounded-3xl overflow-hidden shadow-[0_40px_120px_-40px_rgba(0,0,0,0.8)]">
            {/* Top bar */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-[#0F0F0F]">
              <div className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center">
                  <span className="font-display text-black text-[10px] font-bold">F</span>
                </div>
                <span className="font-display text-white text-sm">Studio</span>
                <span className="text-white/30">/</span>
                <span className="text-[#D4AF37] text-sm">{activeStyle} · concept</span>
              </div>
              <div className="hidden md:flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 border border-white/10 rounded-full px-2 py-0.5 inline-flex items-center gap-1.5">
                  <Lock className="w-2.5 h-2.5" /> Preview
                </span>
                <Link
                  to="/signin"
                  data-testid="dashboard-preview-signin"
                  className="text-xs px-3 py-1.5 rounded-md bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition"
                >
                  Sign in to render
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-12 min-h-[640px]">
              {/* LEFT — Generate panel (interactive but gated) */}
              <aside className="col-span-12 lg:col-span-3 border-r border-white/5 bg-[#0E0E0E] p-5 space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2">Reference sketch</div>
                  <button
                    onClick={handleLockedAction}
                    data-testid="dashboard-preview-upload"
                    className="w-full h-28 rounded-xl border border-dashed border-white/15 bg-[#0B0B0B] hover:border-[#D4AF37]/40 hover:bg-[#141414] transition flex flex-col items-center justify-center gap-1.5 text-white/55 group"
                  >
                    <Upload className="w-4 h-4 group-hover:text-[#D4AF37]" />
                    <div className="text-xs">Drop a sketch</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/30">PNG · JPG</div>
                  </button>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2">Describe the piece</div>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    data-testid="dashboard-preview-prompt"
                    rows={4}
                    placeholder="A bridal Jadau choker with uncut diamonds, layered pearl strings, 22kt gold…"
                    className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition resize-none"
                  />
                </div>

                <button
                  onClick={handleLockedAction}
                  data-testid="dashboard-preview-generate"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37]/90 text-black text-sm font-medium py-3 hover:bg-[#F3CE5A] transition relative group"
                >
                  <Wand2 className="w-4 h-4" /> Generate render
                  <Lock className="w-3 h-3 opacity-60 group-hover:opacity-100" />
                </button>
                <p className="text-[10px] text-white/35 text-center -mt-2">
                  Sign in required to render
                </p>
              </aside>

              {/* CENTER — canvas */}
              <main className="col-span-12 lg:col-span-6 bg-[#0B0B0B] p-5 border-r border-white/5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1.5 p-1 rounded-lg bg-[#141414] border border-white/5">
                    {TABS.map((t) => (
                      <button
                        key={t}
                        onClick={() => setActiveView(t)}
                        data-testid={`dashboard-preview-tab-${t.toLowerCase()}`}
                        className={`text-xs px-3 py-1.5 rounded-md transition ${
                          activeView === t ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "text-white/55 hover:text-white"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-white/40">
                    <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                    Sample render · {activeStyle}
                  </div>
                </div>

                <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[5/3]">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-all duration-700"
                    style={{ backgroundImage: `url(${currentImage})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
                        {activeStyle} · {activeView} view
                      </div>
                      <div className="font-display text-white text-xl mt-1">{activeStyle} concept · Sample</div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-6 gap-2">
                  {Object.entries(STYLE_IMG).slice(0, 6).map(([name, src]) => (
                    <button
                      key={name}
                      onClick={() => setActiveStyle(name)}
                      className={`aspect-square rounded-lg bg-cover bg-center border transition ${
                        activeStyle === name ? "border-[#D4AF37]/60" : "border-white/10 hover:border-white/30"
                      }`}
                      style={{ backgroundImage: `url(${src})` }}
                    />
                  ))}
                </div>
              </main>

              {/* RIGHT — controls */}
              <aside className="col-span-12 lg:col-span-3 bg-[#0E0E0E] p-5 space-y-5 overflow-y-auto">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">Style Preset</div>
                  <div className="flex flex-wrap gap-1.5">
                    {STYLES.map((s) => (
                      <button
                        key={s}
                        onClick={() => setActiveStyle(s)}
                        data-testid={`dashboard-preview-style-${s.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`text-[11px] px-2.5 py-1.5 rounded-full border transition ${
                          activeStyle === s
                            ? "bg-[#D4AF37]/15 border-[#D4AF37]/55 text-[#D4AF37]"
                            : "border-white/10 text-white/60 hover:border-white/25"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">Gemstone</div>
                  <div className="space-y-2">
                    {STONES.map((s, i) => (
                      <div key={s.name} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2.5">
                          <span className="w-3.5 h-3.5 rounded-full border border-white/20" style={{ background: s.color }} />
                          <span className="text-white/65">{s.name}</span>
                        </div>
                        <span className="text-white/35">{i === 0 ? "Primary" : i === 1 ? "Accent" : "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">Specifications</div>
                  <div className="space-y-2 text-xs">
                    {[
                      ["Est. Weight", "18.6 g"],
                      ["Stones", "42 polki"],
                      ["Casting", "Lost-wax"],
                      ["CAD ready", "Yes"],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-white/45">{k}</span>
                        <span className="text-white/80">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleLockedAction}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#D4AF37]/30 text-black/70 text-sm font-medium py-2.5 hover:bg-[#D4AF37]/45 transition"
                >
                  <Download className="w-4 h-4" /> Export CAD <Lock className="w-3 h-3 opacity-70" />
                </button>
              </aside>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
